// Read-through timing (finishing bundle 2B, C12). Pure and framework-free so
// the player and its tests share one source of truth.
//
// Each Script row is spoken at the runtime badge's pace, counting words the
// same way (spoken Audio cells only; On-screen text counts as nothing
// spoken). A row with nothing spoken holds for HOLD_SECONDS instead. The
// total is the badge's unrounded spoken seconds plus the holds. Speed scales
// everything, holds included. Images never affect timing.

import { isSpokenAudioType } from './audioTypes';
import { countWords, WORDS_PER_MINUTE } from './runtime';

export const HOLD_SECONDS = 2;
export const SPEEDS = [0.8, 1, 1.2] as const;
export type ReadThroughSpeed = (typeof SPEEDS)[number];

interface DocNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown> | null;
  content?: DocNode[];
}

/** What the timing needs to know about one row. */
export interface RowFacts {
  /** Spoken words in the Audio cell (0 for On-screen text or empty audio). */
  words: number;
  /** Interview rows get a short breath before the voice reads them. */
  interview: boolean;
  /** Anything at all to show: text in either cell, or an image. */
  hasContent: boolean;
}

export interface ReadThroughSegment {
  /** Index into the facts passed in (skipped empty rows keep their index). */
  rowIndex: number;
  /** Seconds at 1×, from the start of the read-through. */
  start: number;
  duration: number;
  /** True for a row with nothing spoken: a timed hold. */
  hold: boolean;
}

export interface ReadThroughPlan {
  segments: ReadThroughSegment[];
  /** Seconds at 1×. */
  total: number;
}

function textOf(node: DocNode | undefined, out: string[]): void {
  if (!node || typeof node !== 'object') return;
  if (typeof node.text === 'string') out.push(node.text);
  for (const child of node.content ?? []) textOf(child, out);
}

/** Facts for one `scriptRow` JSON node. */
export function rowFacts(row: DocNode): RowFacts {
  const cells = row.content ?? [];
  const audio = cells.find((c) => c.attrs?.kind === 'audio') ?? cells[0];
  const visual = cells.find((c) => c.attrs?.kind === 'visuals') ?? cells[1];
  const audioType = audio?.attrs?.audioType;
  const audioText: string[] = [];
  textOf(audio, audioText);
  const visualText: string[] = [];
  textOf(visual, visualText);
  const spokenText = isSpokenAudioType(audioType) ? audioText.join(' ') : '';
  const hasImage = (visual?.content ?? []).some((n) => n.type === 'refImage');
  return {
    words: countWords(spokenText),
    interview: audioType === 'interview',
    hasContent:
      audioText.join('').trim().length > 0 || visualText.join('').trim().length > 0 || hasImage,
  };
}

/** The Script rows a read-through plays: every top-level scriptRow, in order.
 *  Interview questions and Production notes aren't rows, so they're skipped. */
export function scriptRowsOf(doc: unknown): DocNode[] {
  const root = doc as DocNode | null;
  return (root?.content ?? []).filter((n) => n?.type === 'scriptRow');
}

export function planReadThrough(facts: RowFacts[], wpm = WORDS_PER_MINUTE): ReadThroughPlan {
  const segments: ReadThroughSegment[] = [];
  let at = 0;
  facts.forEach((row, rowIndex) => {
    if (!row.hasContent) return;
    const hold = row.words === 0;
    const duration = hold ? HOLD_SECONDS : (row.words / wpm) * 60;
    segments.push({ rowIndex, start: at, duration, hold });
    at += duration;
  });
  return { segments, total: at };
}

export function planReadThroughForDoc(doc: unknown, wpm = WORDS_PER_MINUTE): ReadThroughPlan {
  return planReadThrough(scriptRowsOf(doc).map(rowFacts), wpm);
}

/** Index of the segment playing at `position` (seconds at 1×). */
export function segmentAt(plan: ReadThroughPlan, position: number): number {
  const { segments } = plan;
  if (segments.length === 0) return -1;
  for (let i = segments.length - 1; i >= 0; i--) {
    if (position >= segments[i]!.start) return i;
  }
  return 0;
}

/** "2:05". Whole seconds, rounded down. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds + 1e-6));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "1×", "0.8×". */
export function formatSpeed(speed: number): string {
  return `${speed}×`;
}
